import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { FaltanteCompra } from '@/lib/supabase/tipos-bd'

/**
 * Compras.
 *
 * La lista de materiales que una cotización aprobada dejó sin stock. Cada uno
 * se puede tomar (en gestión), marcar como recibido —"ya está en inventario"—
 * o descartar. El sistema también los cierra solo cuando el stock de World
 * Office sube; aquí solo se leen y se gestionan a mano.
 */

export type EstadoFaltante = FaltanteCompra['estado']

/** Los faltantes, del más nuevo al más viejo. Por defecto los que faltan por resolver. */
export function useFaltantes(incluirCerrados = false) {
  const consulta = useQuery({
    queryKey: ['compras-faltantes', incluirCerrados],
    staleTime: 30_000,
    queryFn: async (): Promise<FaltanteCompra[]> => {
      let q = supabase
        .from('vista_compras_faltantes')
        .select('*')
        .order('creado_en', { ascending: false })

      // Por defecto solo lo que falta por resolver; con el interruptor, todo.
      if (!incluirCerrados) q = q.in('estado', ['pendiente', 'en_gestion'])

      const { data, error } = await q
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as unknown as FaltanteCompra[]
    },
  })

  return {
    faltantes: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/** Cambia el estado de un faltante. Recibido dispara el aviso a quienes esperan. */
export function useMarcarFaltante() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async (args: { id: string; estado: EstadoFaltante; notas?: string }) => {
      const { error } = await supabase.rpc('fn_marcar_faltante', {
        p_id: args.id,
        p_estado: args.estado,
        p_notas: args.notas ?? null,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['compras-faltantes'] })
      void cliente.invalidateQueries({ queryKey: ['avisos'] })
    },
  })
}
